import BranchDropdown from "@/components/BranchDropdown";
import { TotalSalesChart } from "@/components/TotalSalesChart";
import { Calendar } from "@/components/ui/calendar";
import { Skeleton } from "@/components/ui/skeleton";
import { ProductBranchSlice } from "@/store/BranchSlice";
import { RootState } from "@/store/store";
import { ChartData } from "@/types/globalTypes";
import { AppConfig } from "@/utils/AppConfig";
import { formatMoney } from "@/utils/helpers";
import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { FaCalendarAlt, FaCashRegister, FaChartPie, FaChevronDown, FaEye } from "react-icons/fa";
import { GoDotFill } from "react-icons/go";
import { ImUsers } from "react-icons/im";
import { MdOutlineMenuBook } from "react-icons/md";
import { useSelector } from "react-redux";


export default function DashboardAnalytics() {
    const dateListRef = useRef<HTMLDivElement>(null)
    const dateContainerRef = useRef<HTMLDivElement>(null)
    const [selectedDateStart, setSelectedDateStart] = useState<string | null>(null)
    const [selectedDateEnd, setSelectedDateEnd] = useState<string | null>(null)
    const [selectedTimeStart, setSelectedTimeStart] = useState<number | null>();
    const [selectedTimeEnd, setSelectedTimeEnd] = useState<number | null>();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null)
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const [chartDay, setChartDay] = useState<number | null>(null)
    const [yesterday, setYesterday] = useState<number | null>();
    const [lastWeek, setLastWeek] = useState<number | null>(null)
    const [lastMonth, setLastMonth] = useState<number | null>(null)
    const [chartData, setChartData] = useState<ChartData | null>(null)
    const [lastYear, setLastYear] = useState<number | null>(null)
    const [currentYearStart, setCurrentYearStart] = useState<number | null>(null)
    const [currentYearEnd, setCurrentYearEnd] = useState<number | null>(null)
    const [currentMonthStart, setCurrentMonthStart] = useState<number | null>(null)
    const [currentMonthEnd, setCurrentMonthEnd] = useState<number | null>(null)
    const [filter, setFilter] = useState<string | null>("this month");
    const [dateOpen, setDateOpen] = useState(false);
    const [date, setDate] = useState<DateRange | undefined>({
        from: undefined, // Default start date
        to: undefined, // Default end date (20 days after 2025-01-20)
    });
    const userData = useSelector((state: RootState) => state.user.userData)
    const [loading, setLoading] = useState<boolean>(true);
    const baseUrl = import.meta.env.VITE_BASE_URL
    const token = useSelector((state: { auth: { token: string } }) => state.auth.token);
    const [selectedBranch, setSelectedBranch] = useState<ProductBranchSlice[] | null>(null);
    const formatUnixTimestamp = (unixTimestamp: number | null): string => {
        if (unixTimestamp === null) {
            return "Invalid date"; // Return a default value
        }

        const date = new Date(unixTimestamp * 1000);

        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
    };
    //* turns date to unix time stamp
    const dateToUnix = (date: Date) => {
        return Math.floor(date.getTime() / 1000); // Convert Date to Unix timestamp (seconds)
    };
    //! gets today timestamp
    function getTodayUnixTimestamp() {
        setChartDay(Math.floor(new Date().getTime() / 1000))

    }
    //^ gets yesterday timestamp
    function getYesterdayUnixTimestamp() {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1); // Subtract 1 day
        setYesterday(Math.floor(yesterday.getTime() / 1000)); // Convert to UNIX timestamp (seconds)

    }
    function getLastWeekUnixTimestamp() {
        const lastWeek = new Date();
        lastWeek.setDate(lastWeek.getDate() - 7); // Subtract 7 days to get the same day last week
        setLastWeek(Math.floor(lastWeek.getTime() / 1000)) // Convert to UNIX timestamp (seconds)
    }
    function getLast29DaysUnixTimestamp() {
        const last29Days = new Date();
        last29Days.setDate(last29Days.getDate() - 29); // Subtract 29 days
        setLastMonth(Math.floor(last29Days.getTime() / 1000))// Convert to UNIX timestamp (seconds)
    }
    function getLastYearUnixTimestamp() {
        const lastYear = new Date();
        lastYear.setFullYear(lastYear.getFullYear() - 1); // Subtract 1 year
        setLastYear(Math.floor(lastYear.getTime() / 1000)) // Convert to UNIX timestamp (seconds)
    }
    function getCurrentYearUnixTimestamps() {
        const startOfYear = new Date(new Date().getFullYear(), 0, 1); // January 1st of the current year
        const endOfYear = new Date(new Date().getFullYear(), 11, 31, 23, 59, 59); // December 31st of the current year


        setCurrentYearStart(Math.floor(startOfYear.getTime() / 1000)); // Convert to Unix timestamp
        setCurrentYearEnd(Math.floor(endOfYear.getTime() / 1000)) // Convert to Unix timestamp

    }
    function getCurrentMonthUnixTimestamps() {
        const now = new Date(); // Current date
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1); // Start of the month
        const endOf29Days = new Date(startOfMonth); // Clone startOfMonth date
        endOf29Days.setDate(startOfMonth.getDate() + 28); // Add 28 days for a 29-day range


        setCurrentMonthStart(Math.floor(startOfMonth.getTime() / 1000)); // Convert to Unix timestamp
        setCurrentMonthEnd(Math.floor(endOf29Days.getTime() / 1000)) // Convert to Unix timestamp

    }


    const unixToDate = (unixTimestamp: number | null) => {
        if (!unixTimestamp) return '';
        const date = new Date(unixTimestamp * 1000); // Convert to milliseconds
        const month = date.getMonth() + 1; // Months are 0-based
        const day = date.getDate();
        const year = date.getFullYear();

        return `${month}/${day}/${year}`;
    };
    const openDayList = () => {
        if (!isOpen) {
            if (dateListRef.current) {
                dateListRef.current.classList.remove('hidden');
                dateListRef.current.classList.add('flex');
                setIsOpen(true);
            }
        }
    };
    const handleClickOutside = (event: MouseEvent) => {
        if (
            containerRef.current &&
            !containerRef.current.contains(event.target as Node)
        ) {
            if (dateListRef.current) {
                dateListRef.current.classList.add('hidden');
                dateListRef.current.classList.remove('flex');
                setIsOpen(false);
            }
        }
    };
    /// ~ adding the event to the document 
    useEffect(() => {
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);



    async function filterCharts({ start, end, branches }: { start: number, end: number, branches?: ProductBranchSlice[] }) {
        try {
            const response = await axios.get(`${baseUrl}/api/dashboard/analytics`, {
                params: {
                    startTime: formatUnixTimestamp(start),
                    endTime: formatUnixTimestamp(end),
                    branches:
                        (userData?.role === "owner" || userData?.role === "superadmin") && Array.isArray(branches)
                            ? branches.map((branch) => branch._id)
                            : undefined

                },
                headers: {
                    Authorization: token,
                }
            })


            setChartData(response.data)
            setSelectedDateStart(unixToDate(start))
            setSelectedDateEnd(unixToDate(end))

        } catch (error) {
            console.error(error);

        } finally {
            dateListRef.current?.classList.add('hidden');
            dateListRef.current?.classList.remove('flex');
            setIsOpen(false);
        }
    }

    function openDateList() {
        if (!dateOpen) {
            if (dateContainerRef.current) {
                dateContainerRef.current.classList.remove('hidden');
                dateContainerRef.current.classList.add('flex');
                setDateOpen(true);
            }
        }
    }
    function closeDateList() {
        if (dateOpen) {
            if (dateContainerRef.current) {
                dateContainerRef.current.classList.add('hidden');
                dateContainerRef.current.classList.remove('flex');
                setDateOpen(false);
            }
        }
    }


    async function getCharts() {
        setLoading(true)



        try {
            const response = await axios.get(`${baseUrl}/api/dashboard/analytics`, {
                params: {
                    startTime: formatUnixTimestamp(currentMonthStart),
                    endTime: formatUnixTimestamp(currentMonthEnd),
                },
                headers: {
                    Authorization: token,
                }
            })



            setChartData(response.data)
            setSelectedDateStart(unixToDate(currentMonthStart))
            setSelectedDateEnd(unixToDate(currentMonthEnd))
        } catch (error) {
            console.error(error);

        } finally {
            setLoading(false)
        }
    }


    const [hoveredDate, setHoveredDate] = useState<Date | undefined>();
    const displayRange = useMemo(() => {
        if (date?.from && !date.to && hoveredDate) {
            return {
                from: date.from,
                to: hoveredDate > date.from ? hoveredDate : date.from,
            };
        }
        return date;
    }, [date, hoveredDate]);
    const handleSelect = (selected: DateRange | undefined) => {
        if (!selected?.from) {
            // Reset everything if nothing selected
            setDate({ from: undefined, to: undefined });
            return;
        }

        if (selected.from && selected.to) {
            // Full range selected — commit it
            setDate(selected);
            return;
        }

        if (date?.from && !date.to) {
            // 'from' is already selected, now selecting 'to'
            if (selected.from.getTime() === date.from.getTime()) {
                // Clicked the same date twice → reset
                setDate({ from: undefined, to: undefined });
            } else {
                // Set full range
                setDate({
                    from: date.from,
                    to: selected.from,
                });
            }
        } else {
            // Start a new range
            setDate({ from: selected.from, to: undefined });
        }
    };


    useEffect(() => {
        if (!date?.from) return;

        const startUnix = dateToUnix(date.from);
        const endUnix = date.to ? dateToUnix(date.to) : startUnix; // If same-day, treat as single day

        setSelectedTimeStart(startUnix);
        setSelectedTimeEnd(endUnix);

        filterCharts({
            start: startUnix,
            end: endUnix,
            branches: selectedBranch ?? [],
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    useEffect(() => {

        if (!selectedDateEnd && !selectedDateStart) {
            setSelectedDateStart(unixToDate(lastMonth))
            setSelectedDateEnd(unixToDate(chartDay))
        }
        getTodayUnixTimestamp()
        getYesterdayUnixTimestamp()
        getLastWeekUnixTimestamp()
        getLast29DaysUnixTimestamp()
        getLastYearUnixTimestamp()
        getCurrentYearUnixTimestamps()
        getCurrentMonthUnixTimestamps()
        if (chartDay && lastMonth) {
            if (userData?.role === "owner" || userData?.role === "superadmin" || userData?.role === "admin") {
                getCharts()
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartDay])



    useEffect(() => {


        if (selectedBranch == null) {
            setSelectedBranch(null);


        }
    }, [selectedBranch])
    if (loading) {
        return (
            <>
                <div>
                    <Skeleton className="w-[500px] h-[50px] rounded-md" />
                </div>
                <div className="flex gap-5 flex-wrap mt-10">
                    <Skeleton className="w-[200px] h-[100px] dark:bg-slate-600 dark:bg-opacity-35 rounded-md" />
                    <Skeleton className="w-[200px] h-[100px] dark:bg-slate-600 dark:bg-opacity-35 rounded-md" />
                    <Skeleton className="w-[200px] h-[100px] dark:bg-slate-600 dark:bg-opacity-35 rounded-md" />
                </div>
                <div className="mt-10">
                    <Skeleton className="w-full h-[500px] dark:bg-slate-600 dark:bg-opacity-35 rounded-md" />
                </div>
            </>
        )
    }
    return (
        <>
            {(userData?.role === "owner" || userData?.role === "superadmin") && (
                <BranchDropdown
                    filterCharts={filterCharts}
                    setFilter={setFilter}
                    chartDay={chartDay}
                    lastMonth={lastMonth}
                    currentMonthStart={selectedTimeStart ? selectedTimeStart : currentMonthStart}
                    currentMonthEnd={selectedTimeEnd ? selectedTimeEnd : currentMonthEnd}
                    selectedBranch={selectedBranch}
                    setSelectedBranch={setSelectedBranch}
                />
            )}

            <div ref={containerRef} onClick={openDayList} className="relative w-fit bg-white">
                <div onClick={(e) => {
                    e.preventDefault();
                    if (isOpen) {
                        if (!dateListRef.current) return;
                        dateListRef.current.classList.add('hidden');
                        dateListRef.current.classList.remove('flex');
                        setIsOpen(false);

                    }
                }} className="flex justify-center md:text-base text-sm whitespace-nowrap w-fit md:px-5 px-2 gap-1 py-2 items-center md:gap-5 border rounded-lg cursor-pointer">
                    <div className="flex items-center  gap-2">
                        <span><FaCalendarAlt /></span>
                        <span className="text-primary font-semibold"> {t("reports.Reporthistory")}</span>
                    </div>
                    <span className="text-slate-400">{selectedDateStart} - {selectedDateEnd}</span>
                    <FaChevronDown className="text-sm text-slate-400" />
                </div>
                <div ref={dateListRef} className={` w-fit  bg-gray-50 z-50 left-0 h-fit gap-5 flex-col lg:flex-row md:absolute hidden  ${lang === "en" ? "left-0" : "right-0"}`}>
                    <ul className={`text-gray-500  w-fit md:w-[200px] text-sm  h-fit rounded-lg border bg-white  `}>
                        <li onClick={() => {
                            closeDateList()
                            if (chartDay !== null && lastMonth !== null) {
                                filterCharts({ start: chartDay, end: chartDay, branches: selectedBranch || [] })
                                setSelectedTimeStart(chartDay)
                                setSelectedTimeEnd(chartDay)
                            }
                            setFilter("today")
                        }} className={`flex items-center gap-2 hover:text-black ${filter === "today" && " bg-skyblue"} hover:bg-skyblue  cursor-pointer px-5  py-2 transition-all duration-200`}> <GoDotFill className="text-[15px]" />{t("reports.Today")}</li>
                        <li onClick={() => {
                            closeDateList()
                            if (yesterday !== null && yesterday !== undefined) {
                                filterCharts({ start: yesterday, end: yesterday, branches: selectedBranch || [] })
                                setSelectedTimeStart(yesterday)
                                setSelectedTimeEnd(yesterday)
                            }
                            setFilter("yesterday")
                        }} className={`flex items-center gap-2 hover:text-black ${filter === "yesterday" && " bg-skyblue"} hover:bg-skyblue  cursor-pointer px-5  py-2 transition-all duration-200`}> <GoDotFill className="text-[15px]" />{t("reports.YesterDay")}</li>
                        <li onClick={() => {
                            closeDateList()
                            if (lastWeek !== null && lastWeek !== undefined && chartDay !== null) {
                                filterCharts({ start: lastWeek, end: chartDay, branches: selectedBranch || [] })
                                setSelectedTimeStart(lastWeek)
                                setSelectedTimeEnd(chartDay)
                            }
                            setFilter("last week")
                        }} className={`flex items-center gap-2 hover:text-black ${filter === "last week" && " bg-skyblue"} hover:bg-skyblue  cursor-pointer px-5  py-2 transition-all duration-200`}> <GoDotFill className="text-[15px]" />{t("reports.LastWeek")}</li>
                        <li onClick={() => {
                            closeDateList()
                            if (lastMonth !== null && lastMonth !== undefined && chartDay !== null) {
                                filterCharts({ start: lastMonth, end: chartDay, branches: selectedBranch || [] })
                                setSelectedTimeStart(lastMonth)
                                setSelectedTimeEnd(chartDay)
                            }
                            setFilter("last month")
                        }} className={`flex items-center gap-2 hover:text-black ${filter === "last month" && " bg-skyblue"} hover:bg-skyblue cursor-pointer px-5  py-2 transition-all duration-200`}> <GoDotFill className="text-[15px]" />{t("reports.LastMonth")}</li>
                        <li onClick={() => {
                            closeDateList()
                            if (currentMonthEnd !== null && currentMonthStart !== null) {
                                filterCharts({ start: currentMonthStart, end: currentMonthEnd, branches: selectedBranch || [] })
                                setSelectedTimeStart(currentMonthStart)
                                setSelectedTimeEnd(currentMonthEnd)
                            }
                            setFilter("this month")
                        }} className={`flex items-center gap-2 hover:text-black ${filter === "this month" && " bg-skyblue"} hover:bg-skyblue cursor-pointer px-5  py-2 transition-all duration-200`}> <GoDotFill className="text-[15px]" />{t("reports.thisMonth")}</li>
                        <li onClick={() => {
                            closeDateList()
                            if (currentYearStart !== null && currentYearEnd !== null) {
                                filterCharts({ start: currentYearStart, end: currentYearEnd, branches: selectedBranch || [] })
                                setSelectedTimeStart(currentYearStart)
                                setSelectedTimeEnd(currentYearEnd)
                            }
                            setFilter("this year")
                        }} className={`flex items-center gap-2 hover:text-black ${filter === "this year" && " bg-skyblue"} hover:bg-skyblue  cursor-pointer px-5  py-2 transition-all duration-200`}> <GoDotFill className="text-[15px]" />{t("reports.thisyear")}</li>
                        <li onClick={() => {
                            closeDateList()
                            if (lastYear !== null && lastYear !== undefined && chartDay !== null) {
                                filterCharts({ start: lastYear, end: chartDay, branches: selectedBranch || [] })
                                setSelectedTimeStart(lastYear)
                                setSelectedTimeEnd(chartDay)
                            }
                            setFilter("last year")
                        }} className={`flex items-center gap-2 hover:text-black ${filter === "last year" && " bg-skyblue"} hover:bg-skyblue  cursor-pointer px-5  py-2 transition-all duration-200`}> <GoDotFill className="text-[15px]" />{t("reports.lastyear")}</li>
                        <li onClick={() => {
                            setFilter("custom")
                            openDateList()
                        }} className={`flex items-center gap-2 hover:text-black ${filter === "custom" && " bg-skyblue"} hover:bg-skyblue   cursor-pointer px-5  py-2 transition-all duration-200`}> <GoDotFill className="text-[15px]" /> {t("reports.ChooseAperiod")}</li>
                    </ul>
                    <div ref={dateContainerRef} className="z-50 border rounded-md hidden">
                        <Calendar
                            disabled={{ after: new Date() }}
                            initialFocus
                            toYear={new Date().getFullYear()}
                            mode="range"
                            selected={displayRange}
                            onSelect={handleSelect}
                            onDayMouseEnter={(date) => setHoveredDate(date)}
                            onDayMouseLeave={() => setHoveredDate(undefined)}
                            numberOfMonths={2}
                            className="border p-3 rounded-lg bg-white dark:bg-white text-black" // Ensure light mode styles
                            classNames={{
                                day_range_start: "bg-sidebar rounded-none text-skyblue hover:text-white hover:bg-sidebar",
                                day_range_end: "bg-sidebar rounded-none text-skyblue hover:text-white hover:bg-sidebar",
                                day_range_middle: "bg-skyblue rounded-none dark:bg-skyblue text-black hover:text-slate-900 hover:bg-skyblue",
                                day_selected: "bg-black rounded-none text-black hover:bg-black hover:text-white",
                                day_today: "border rounded-none border-sidebar text-black hover:text-slate-900 hover:bg-skyblue",
                                nav_button_next: "bg-white dark:bg-white border-0",
                                nav_button_previous: "bg-white dark:bg-white rotate-180 border-0 ",
                            }}
                            dir={lang === "ar" ? "rtl" : "ltr"}
                        />
                    </div>

                </div>
            </div>


            <div className="mt-10">
                <div className="cards-container w-[100%] md:mx-0 justify-center md:justify-normal mx-auto whitespace-nowrap flex flex-wrap gap-5 mt-10">
                    <div className="flex items-center gap-2 md:gap-5 md:flex-wrap w-full md:w-fit">
                        <div className="md:w-[200px] px-2 md:px-5 py-3 w-full bg-white flex justify-between flex-col h-fit gap-3 border rounded-xl">
                            <div className="flex justify-between items-center">
                                <span className="">{t("Dashboard.TotalVisits")}</span>
                                <span className="rounded-full p-3 h-fit text-slate-400">
                                    <FaEye className=" text-sm md:text-xl" />
                                </span>
                            </div>
                            <div className="flex flex-col gap-2">
                                <span className="text-sm lg:text-lg font-semibold">{chartData?.visits} {t("Dashboard.MenuVisits")}</span>
                            </div>
                        </div>
                        <div className="md:w-[200px] px-2 md:px-5 py-3 w-full bg-white flex h-fit flex-col gap-3 border rounded-xl">
                            <div className="flex justify-between items-center">
                                <span className="">{t("Dashboard.TotalOrders")}</span>
                                <span className="rounded-full p-3 h-fit text-slate-400">
                                    <ImUsers className=" text-sm md:text-xl" />
                                </span>
                            </div>
                            <div className="flex flex-col gap-2">
                                <span className="text-sm lg:text-lg font-semibold">{chartData?.orders} {t("Dashboard.Orders")}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 md:gap-5 md:flex-wrap w-full md:w-fit">
                        {chartData?.menuSales && <>
                            <div className="md:w-[200px] px-2 md:px-5 py-3 w-full bg-white flex h-fit flex-col gap-3 border rounded-xl">
                                <div className="flex justify-between items-center">
                                    <span className="">{t("Dashboard.MenuSales")}</span>
                                    <span className="rounded-full p-3 h-fit text-slate-400">
                                        <MdOutlineMenuBook className=" text-sm md:text-xl" />
                                    </span>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <span dir="rtl" className="text-sm lg:text-lg font-semibold">
                                        {formatMoney(chartData?.menuSales)} {AppConfig.currency.icon()}
                                    </span>
                                </div>
                            </div></>}
                        {chartData?.cashierSales && <>
                            <div className="md:w-[200px] px-2 md:px-5 py-3 w-full bg-white flex h-fit flex-col gap-3 border rounded-xl">
                                <div className="flex justify-between items-center">
                                    <span className="">{t("Dashboard.CashierSales")}</span>
                                    <span className="rounded-full p-3 h-fit text-slate-400">
                                        <FaCashRegister className=" text-sm md:text-xl" />
                                    </span>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <span dir="rtl" className="text-sm lg:text-lg font-semibold">
                                        {formatMoney(chartData?.cashierSales)} {AppConfig.currency.icon()}
                                    </span>
                                </div>
                            </div></>}
                    </div>
                    <div className="md:w-[200px] px-5 py-3 w-full bg-white flex h-fit flex-col gap-3 border rounded-xl">
                        <div className="flex justify-between items-center">
                            <span className="">{t("Dashboard.TotalSales")}</span>
                            <span className="rounded-full p-3 h-fit text-slate-400">
                                <FaChartPie className="text-xl" />
                            </span>
                        </div>
                        <div className="flex flex-col gap-2">
                            <span dir="rtl" className="text-lg font-semibold">
                                {chartData?.sales && formatMoney(chartData?.sales)} {AppConfig.currency.icon()}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className=" w-full mx-auto mt-10 ">
                {chartData && <TotalSalesChart data={chartData.analytics} />}
            </div></>
    )
}
